import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

// A small fox, modelled facing +X. Two variants are colour morphs (red and
// arctic), and a looping "idle-bob" nudges the body's POSITION up and down for
// a breathing-at-rest feel — the example for the position channel on a
// creature. The legs stay planted (static); everything above is named "body".
interface Coat { fur: string; furDark: string; belly: string; ear: string; tailTip: string; }

function foxParts(coat: Coat): () => Part[] {
  return () => {
    const parts: Part[] = [];
    for (const [x, z] of [[0.05, 0.022], [0.05, -0.022], [-0.05, 0.022], [-0.05, -0.022]] as const) {
      parts.push({ geometry: new THREE.CylinderGeometry(0.007, 0.006, 0.065, 4), color: coat.furDark, position: [x, 0.0325, z] });
    }
    const body = (p: Part): Part => ({ ...p, name: "body" });
    parts.push(
      body({ geometry: new THREE.IcosahedronGeometry(0.038, 0), color: coat.fur, position: [0, 0.065, 0], scale: [1.7, 0.95, 0.85] }),
      body({ geometry: new THREE.IcosahedronGeometry(0.028, 0), color: coat.fur, position: [0.075, 0.082, 0] }),
      body({ geometry: new THREE.ConeGeometry(0.012, 0.03, 4), color: coat.fur, position: [0.1, 0.078, 0], rotation: [0, 0, -Math.PI / 2] }),
      body({ geometry: new THREE.ConeGeometry(0.01, 0.022, 4), color: coat.ear, position: [0.07, 0.108, 0.016] }),
      body({ geometry: new THREE.ConeGeometry(0.01, 0.022, 4), color: coat.ear, position: [0.07, 0.108, -0.016] }),
      body({ geometry: new THREE.IcosahedronGeometry(0.022, 0), color: coat.belly, position: [0.04, 0.05, 0], scale: [1.2, 0.9, 1.2] }),
      body({ geometry: new THREE.CylinderGeometry(0.01, 0.022, 0.075, 5), color: coat.furDark, position: [-0.085, 0.07, 0], rotation: [0, 0, 1.0] }),
      body({ geometry: new THREE.IcosahedronGeometry(0.016, 0), color: coat.tailTip, position: [-0.115, 0.092, 0] }),
    );
    return parts;
  };
}

export const fox = defineModel({
  id: "fox",
  batchId: "standard",
  name: "Fox",
  tags: ["creature", "animal", "mammal", "animated"],
  summary: "A low-slung fox in red or arctic coats, breathing at rest.",
  author: "age-of-mcp",
  footprint: 0.1,
  variants: [
    { id: "red", label: "Red fox", parts: foxParts({ fur: "#c4622a", furDark: "#7a3d18", belly: "#e8e2d4", ear: "#3a2012", tailTip: "#efe9dc" }) },
    { id: "arctic", label: "Arctic fox", parts: foxParts({ fur: "#e9eef2", furDark: "#b9c4cc", belly: "#ffffff", ear: "#cdd6dc", tailTip: "#ffffff" }) },
  ],
  animations: [
    {
      id: "idle-bob",
      label: "Idle bob",
      duration: 2.6,
      loop: true,
      tracks: [
        {
          target: "body",
          position: [
            { t: 0, value: [0, 0, 0] },
            { t: 1.3, value: [0, 0.006, 0], ease: "sine" },
            { t: 2.6, value: [0, 0, 0], ease: "sine" },
          ],
        },
      ],
    },
  ],
});
